/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import {
  useElements,
  useStripe,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import { Formik, Form, Field } from 'formik';
import { useAssignLicenseMutation } from 'renderer/features/products-page/services/products.endpoints';
import { useRedeemResellerCodeMutation } from 'renderer/features/resellerCodes-page/services/resellerCodes.endpoints';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import StripeCreditCardForm from 'renderer/features/purchase-page/components/StripeCreditCardForm';
import { useListPaymentMethodsQuery } from 'renderer/features/settings/services/paymentMethods.endpoints';
import BillingAddressForm from 'renderer/features/settings/components/BillingAddressForm';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import * as z from 'zod';

// Define the regex for reseller codes
const resellerCodeRegex = /^[a-zA-Z0-9]+-[A-Z0-9]{8}-\d{1,2}$/;

// Define Zod schemas for each case
const generalSchema = z
  .object({
    inputValue: z.string().min(1, 'Input value is required'),
  })
  .describe('generalSchema');

const resellerCodeWithDefaultSchema = z
  .object({
    inputValue: z
      .string()
      .regex(resellerCodeRegex, 'Invalid reseller code format'),
  })
  .describe('resellerCodeWithDefaultSchema');

const resellerCodeWithoutDefaultSchema = z
  .object({
    inputValue: z
      .string()
      .regex(resellerCodeRegex, 'Invalid reseller code format'),
    newPaymentMethodToken: z
      .string()
      .min(1, 'Payment method token is required'),
    nameOnCard: z.string().min(1, 'Name on card is required'),
    streetAddress: z.string().min(1, 'Street address is required'),
    locality: z.string().min(1, 'City is required'),
    region: z.string().min(1, 'State/Region is required'),
    postalCode: z.string().min(1, 'Postal code is required'),
    countryCode: z.string().min(1, 'Country code is required'),
    numberValidity: z.string().min(1, 'Card number is invalid'),
    cvvValidity: z.string().min(1, 'CVC number is invalid'),
    expirationDateValidity: z.string().min(1, 'Expiration date is invalid'),
  })
  .describe('resellerCodeWithoutDefaultSchema');

// Validation function using `toValidator`
// const getValidationSchema = (
//   inputValue: string,
//   hasDefaultPayment: boolean,
// ) => {
//   if (resellerCodeRegex.test(inputValue)) {
//     return hasDefaultPayment
//       ? toValidator(resellerCodeWithDefaultSchema)
//       : toValidator(resellerCodeWithoutDefaultSchema);
//   }
//   return toValidator(generalSchema);
// };

// const getValidationSchema = (
//   inputValue: string,
//   hasDefaultPaymentMethod: boolean,
// ) => {
//   const isResellerCode = resellerCodeRegex.test(inputValue);

//   let schema;
//   if (isResellerCode && hasDefaultPaymentMethod) {
//     schema = resellerCodeWithDefaultSchema;
//   } else if (isResellerCode && !hasDefaultPaymentMethod) {
//     schema = resellerCodeWithoutDefaultSchema;
//   } else {
//     schema = generalSchema;
//   }

//   return {
//     schema,
//     validate: (values: any) => {
//       const result = schema.safeParse(values);
//       if (result.success) return {};
//       return {
//         ...result.error.formErrors.fieldErrors,
//         _schemaName: schema.description,
//       };
//     },
//   };
// };

interface AddLicenseDialogProps {
  open: boolean;
  serialType: 'license' | 'subscription';
  onClose: () => void;
  email: string;
  userId: string;
  refetchUserDetails: () => void;
  paymentMethods: any[];
}

const AddLicenseDialog: React.FC<AddLicenseDialogProps> = ({
  open,
  serialType,
  onClose,
  email,
  userId,
  refetchUserDetails,
  paymentMethods,
}) => {
  const [showCreditCardForm, setShowCreditCardForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [assignLicense, { isLoading: assignLoading, error: assignError }] =
    useAssignLicenseMutation();
  const [redeemResellerCode, { isLoading: redeemLoading, error: redeemError }] =
    useRedeemResellerCodeMutation();
  console.log('redeemError', redeemError);
  // const { data: paymentMethods, isLoading: paymentMethodsLoading } =
  //   useListPaymentMethodsQuery('Subscription');

  const stripe = useStripe();
  const elements = useElements();

  const defaultPaymentMethod = paymentMethods?.find((method) => method.default);
  const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: any,
  ) => {
    const { value } = e.target;
    setFieldValue('inputValue', value);

    const isResellerCode = resellerCodeRegex.test(value);
    setShowCreditCardForm(isResellerCode && !defaultPaymentMethod);
  };

  const handleSubmit = async (values: any, { resetForm }: any) => {
    const {
      inputValue,
      nameOnCard,
      streetAddress,
      locality,
      region,
      postalCode,
      countryName,
    } = values;
    const billingAddress = {
      nameOnCard,
      streetAddress,
      locality,
      region,
      postalCode,
      countryName,
    };
    const isResellerCode = resellerCodeRegex.test(inputValue);

    try {
      if (isResellerCode) {
        if (!defaultPaymentMethod && stripe && elements) {
          const cardElement = elements.getElement(CardNumberElement);
          if (!cardElement) return;

          const result = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
          });

          if (result.error) {
            console.error('Stripe error:', result.error.message);
            return;
          }

          const response = await redeemResellerCode({
            userId,
            code: inputValue,
            email,
            token: result.paymentMethod?.id,
            billingAddress,
          }).unwrap();
          console.log('response', response);
        } else {
          const response = await redeemResellerCode({
            userId,
            code: inputValue,
            email,
            paymentMethodId: defaultPaymentMethod?._id,
            billingAddress,
          }).unwrap();
          console.log('response', response);
        }

        setSuccessMessage('Reseller code redeemed successfully!');
      } else {
        await assignLicense({ email, serial: inputValue }).unwrap();
        setSuccessMessage('License added successfully!');
      }

      resetForm();
      setShowCreditCardForm(false);
      refetchUserDetails(); // Force refetch after mutation
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    if (!open) {
      setSuccessMessage('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Formik
        initialValues={{
          inputValue: '',
        }}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Form>
            <DialogTitle>
              {serialType === 'license' ? 'Add Serial' : 'Add Subscription Key'}
            </DialogTitle>
            <DialogContent>
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {successMessage}
                </Alert>
              )}
              {assignError && (
                <Alert severity="error">{formatQueryError(assignError)}</Alert>
              )}
              {redeemError && (
                <Alert severity="error">{formatQueryError(redeemError)}</Alert>
              )}
              <Field
                as={TextField}
                name="inputValue"
                fullWidth
                margin="normal"
                label={
                  serialType === 'license'
                    ? 'License Serial or Reseller Code'
                    : 'Subscription Key'
                }
                value={values.inputValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(e, setFieldValue)
                }
              />
              {showCreditCardForm && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No payment methods found. Please enter your payment details.
                  </Alert>
                  <StripeCreditCardForm />

                  <Typography variant="h6" sx={{ my: 2 }}>
                    Billing Address
                  </Typography>
                  <BillingAddressForm disabled={redeemLoading} />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button sx={{ mr: 1 }} variant="outlined" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={assignLoading || redeemLoading}
                type="submit"
              >
                {assignLoading || redeemLoading ? 'Processing...' : 'Save'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddLicenseDialog;
